import React, { useState, useRef } from 'react';
import { CameraIcon } from '@heroicons/react/24/outline';

interface AvatarProps {
  src?: string;
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  editable?: boolean;
  onImageChange?: (file: File) => void;
  className?: string;
}

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  size = 'md',
  editable = false,
  onImageChange,
  className = '',
}) => {
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    sm: 'h-8 w-8 text-sm',
    md: 'h-12 w-12 text-lg',
    lg: 'h-16 w-16 text-xl',
    xl: 'h-24 w-24 text-2xl',
  };

  const getInitials = (fullName: string) => {
    const names = fullName.trim().split(' ');
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const handleImageClick = () => {
    if (editable && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (editable && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      handleImageClick();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onImageChange) {
      onImageChange(file);
    }
  };

  const shouldShowImage = src && !imageError;

  // Construir URL completa se src for um caminho relativo
  const imageUrl =
    src && typeof src === 'string' && src.startsWith('/uploads/') ? getBackendUrl(src) : src;

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        className={`
          ${sizeClasses[size]}
          rounded-full
          flex items-center justify-center
          font-semibold
          ${shouldShowImage ? 'bg-transparent' : 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'}
          ${editable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
          overflow-hidden
          shadow-lg
        `}
        onClick={handleImageClick}
        onKeyDown={handleKeyDown}
        role={editable ? 'button' : undefined}
        tabIndex={editable ? 0 : undefined}
        aria-label={editable ? `Alterar foto de ${name}` : undefined}
      >
        {shouldShowImage ? (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          getInitials(name)
        )}
      </div>

      {editable && (
        <>
          <div
            className="absolute inset-0 rounded-full bg-black bg-opacity-0 hover:bg-opacity-30 flex items-center justify-center transition-all duration-200 cursor-pointer"
            onClick={handleImageClick}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Alterar foto de ${name}`}
          >
            <CameraIcon className="h-6 w-6 text-white opacity-0 hover:opacity-100 transition-opacity" />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

export default Avatar;
